import React, { useEffect, useMemo, useState } from 'react';
import toast from 'react-hot-toast';
import { complianceService } from '../../../services/adminService';

interface DSRItem {
  id: string;
  subject_type: 'affiliate' | 'customer' | 'other';
  type: 'access' | 'erasure' | 'rectification' | 'export' | string;
  email?: string;
  status?: 'open' | 'in_progress' | 'closed' | 'rejected' | string;
  notes?: string;
  created_at?: string;
}

const DSRList: React.FC = () => {
  const [items, setItems] = useState<DSRItem[]>([]);
  const [loading, setLoading] = useState(false);
  const [statusFilter, setStatusFilter] = useState<'' | 'open' | 'in_progress' | 'closed'>('');
  const [emailSearch, setEmailSearch] = useState('');

  const load = async () => {
    setLoading(true);
    try {
      const res = await complianceService.getDSRRequests({ status: statusFilter || undefined, email: emailSearch || undefined, limit: 50 });
      setItems(res || []);
    } catch (e: any) {
      toast.error(e?.message || 'Failed to load DSRs');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const filtered = useMemo(() => items, [items]);

  const updateStatus = async (id: string, status: string) => {
    try {
      await complianceService.updateDSRStatus(id, status as any);
      toast.success('Status updated');
      await load();
    } catch (e: any) {
      toast.error(e?.message || 'Failed to update');
    }
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h2 className="text-xl font-medium">Data Subject Requests</h2>
        <div className="flex gap-2">
          <input
            value={emailSearch}
            onChange={(e) => setEmailSearch(e.target.value)}
            placeholder="Filter by email"
            className="border rounded px-3 py-2"
          />
          <select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value as '' | 'open' | 'in_progress' | 'closed')} className="border rounded px-3 py-2">
            <option value="">All</option>
            <option value="open">Open</option>
            <option value="in_progress">In Progress</option>
            <option value="closed">Closed</option>
          </select>
          <button onClick={load} className="px-3 py-2 text-sm rounded border">Apply</button>
        </div>
      </div>

      {loading ? (
        <div>Loading...</div>
      ) : (
        <div className="overflow-auto border rounded">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-4 py-2 text-left text-xs font-medium text-gray-500">ID</th>
                <th className="px-4 py-2 text-left text-xs font-medium text-gray-500">Subject</th>
                <th className="px-4 py-2 text-left text-xs font-medium text-gray-500">Type</th>
                <th className="px-4 py-2 text-left text-xs font-medium text-gray-500">Email</th>
                <th className="px-4 py-2 text-left text-xs font-medium text-gray-500">Status</th>
                <th className="px-4 py-2 text-left text-xs font-medium text-gray-500">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200 bg-white">
              {filtered.length === 0 && (
                <tr>
                  <td colSpan={6} className="px-4 py-6 text-center text-sm text-gray-500">No DSRs</td>
                </tr>
              )}
              {filtered.map((it) => (
                <tr key={it.id}>
                  <td className="px-4 py-2 text-sm">{it.id}</td>
                  <td className="px-4 py-2 text-sm">{it.subject_type}</td>
                  <td className="px-4 py-2 text-sm">{it.type}</td>
                  <td className="px-4 py-2 text-sm">{it.email || '-'}</td>
                  <td className="px-4 py-2 text-sm">{it.status}</td>
                  <td className="px-4 py-2 text-sm">
                    <select
                      aria-label={`update-status-${it.id}`}
                      defaultValue={(it.status as 'open' | 'in_progress' | 'closed') || 'open'}
                      onChange={(e) => updateStatus(it.id, e.target.value as 'open' | 'in_progress' | 'closed')}
                      className="border rounded px-2 py-1"
                    >
                      <option value="open">Open</option>
                      <option value="in_progress">In Progress</option>
                      <option value="closed">Closed</option>
                    </select>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

export default DSRList;
